import { useMemo, useState } from 'react';
import { FileText, FlaskConical } from 'lucide-react';
import { AutoListForm } from '@/components/autoforms/AutoListForm';
import { AutoObjectForm } from '@/components/autoforms/AutoObjectForm';
import { PsyPatientCabinet } from '@/components/psy/PsyPatientCabinet';
import { TabBar } from '@/components/ui/TabBar';
import { UniversalWindow } from '@/components/ui/UniversalWindow';
import type { CabinetAppointment } from '@/lib/cabinet/appointments';
import { PSY_CONCLUSIONS } from '@/lib/cabinet/appointments';

export type AppointmentRelatedMode = 'conclusions' | 'tests';

interface Editor {
  id?: string;
  patientId: string;
  sessionId: string;
}

export interface CabinetAppointmentRelatedDialogProps {
  open: boolean;
  mode: AppointmentRelatedMode;
  appointment: CabinetAppointment | null;
  psyInstalled: boolean;
  onClose: () => void;
}

function MissingState({ title, text }: { title: string; text: string }) {
  return (
    <div className="flex h-full items-center justify-center p-6">
      <div className="max-w-md space-y-1 text-center">
        <div className="text-sm font-medium">{title}</div>
        <div className="text-xs text-muted-foreground">{text}</div>
      </div>
    </div>
  );
}

export function CabinetAppointmentRelatedDialog({
  open, mode, appointment, psyInstalled, onClose,
}: CabinetAppointmentRelatedDialogProps) {
  const [scope, setScope] = useState<'session' | 'patient'>('patient');
  const [listKey, setListKey] = useState(0);
  const [editor, setEditor] = useState<Editor | null>(null);

  const patientId = appointment?.patientId || '';
  const patientName = appointment?.patientName || 'Пациент';
  const sessionId = appointment?.id || '';

  const serverFilters = useMemo(() => {
    if (!patientId) return [];
    const filters = [{ field: 'patient_id', condition: '=' as const, value: patientId }];
    if (scope === 'session' && sessionId) {
      filters.push({ field: 'session_id', condition: '=' as const, value: sessionId });
    }
    return filters;
  }, [patientId, scope, sessionId]);

  const title = mode === 'tests'
    ? `Тесты и результаты: ${patientName}`
    : `Заключения: ${patientName}`;

  const handleClose = () => {
    setScope('patient');
    setEditor(null);
    setListKey((k) => k + 1);
    onClose();
  };

  return (
    <>
      <UniversalWindow
        open={open}
        onClose={handleClose}
        title={title}
        blocking
        width="w-[min(980px,96vw)]"
        height="h-[min(82vh,760px)]"
        topBar={mode === 'conclusions' && patientId && psyInstalled ? (
          <div className="flex items-center gap-2 px-3 py-1.5">
            <TabBar
              tabs={sessionId ? [
                { id: 'session', label: 'Этот приём' },
                { id: 'patient', label: 'Все заключения пациента' },
              ] : [
                { id: 'patient', label: 'Все заключения пациента' },
              ]}
              active={scope}
              onChange={(id) => { setScope(id as 'session' | 'patient'); setListKey((k) => k + 1); }}
              group="cabinet-appointment-conclusions-scope"
            />
          </div>
        ) : undefined}
      >
        {mode === 'tests' ? (
          !psyInstalled ? (
            <MissingState
              title="Модуль психологии не установлен"
              text="Тесты и результаты доступны после установки поставки hydra.medic.psy."
            />
          ) : !patientId ? (
            <MissingState title="Пациент не указан" text="Привяжите запись к пациенту, чтобы открыть его тесты и результаты." />
          ) : (
            <div className="h-full min-h-0">
              <PsyPatientCabinet patientId={patientId} patientName={patientName} canManage={false} />
            </div>
          )
        ) : !psyInstalled ? (
          <MissingState
            title="Модуль психологии не установлен"
            text="Заключения доступны после установки поставки hydra.medic.psy."
          />
        ) : !patientId ? (
          <MissingState title="Пациент не указан" text="Привяжите запись к пациенту, чтобы увидеть его заключения." />
        ) : (
          <div className="flex h-full min-h-0 flex-col">
            <div className="shrink-0 border-b bg-background/60 px-3 py-1.5 text-[11px] text-muted-foreground">
              <FileText className="mr-1 inline h-3 w-3" />
              {scope === 'session' ? 'Заключения по этому приёму' : 'Все заключения пациента'}
            </div>
            <div className="min-h-0 flex-1">
              <AutoListForm
                key={`${patientId}:${scope}:${listKey}`}
                objectType="catalog"
                objectName={PSY_CONCLUSIONS}
                serverFilters={serverFilters}
                viewMode="list"
                onRowDoubleClick={(row) => setEditor({
                  id: String(row.id || ''),
                  patientId,
                  sessionId,
                })}
                onCreate={() => setEditor({ patientId, sessionId })}
              />
            </div>
            <div className="shrink-0 border-t bg-muted/20 px-3 py-1.5 text-[10px] text-muted-foreground">
              <FlaskConical className="mr-1 inline h-3 w-3" />
              Двойной щелчок открывает заключение; «Создать» привязывает его к пациенту и приёму.
            </div>
          </div>
        )}
      </UniversalWindow>

      {editor && (
        <AutoObjectForm
          objectType="catalog"
          objectName={PSY_CONCLUSIONS}
          objectId={editor.id}
          initialData={editor.id ? undefined : {
            patient_id: editor.patientId,
            session_id: editor.sessionId,
            conclusion_date: new Date().toISOString().slice(0, 10),
          }}
          onClose={() => {
            setEditor(null);
            setListKey((k) => k + 1);
          }}
        />
      )}
    </>
  );
}
